import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SEOHead from "../../components/shared/SEOHead.jsx";
import JsonLd from "../../components/shared/JsonLd.jsx";
import { buildBreadcrumbJsonLd } from "../../utils/jsonld.js";
import ResponsiveImage from "../../components/shared/ResponsiveImage.jsx";
import ProgressBar from "../../components/spenden/ProgressBar.jsx";
import { getSpendenProjectsForLang } from "../../utils/content.js";
import { SUPPORT_PATH, SUPPORT_SUBPAGE_PATH } from "../Support/paths.js";

const SPENDEN_PATH = { de: "spenden", en: "donate" };

function ProjectCard({ project, lang, t, featured }) {
  const f = project.frontmatter;
  const pct = Math.min(100, Math.round((f.raised / f.goal) * 100));
  const isCompleted = f.status === "completed";

  return (
    <div
      className={featured ? "tl-card-light tl-grid-2" : "tl-card-light"}
      style={
        featured
          ? { display: "grid", gridTemplateColumns: "1.4fr 1fr" }
          : { display: "flex", flexDirection: "column" }
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: featured ? "16px" : "12px", padding: featured ? "40px" : "24px" }}>
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <span className="tl-badge">{isCompleted ? t("spenden.statusCompleted") : t("nav.spenden")}</span>
        </div>
        <span
          style={{
            fontFamily: "var(--font-serif)",
            fontSize: featured ? "30px" : "20px",
            lineHeight: 1.2,
            color: "var(--color-ink)",
          }}
        >
          {f.title}
        </span>
        {featured && (
          <p style={{ fontSize: "15px", lineHeight: 1.7, color: "rgba(10,9,7,0.7)", maxWidth: "52ch", margin: 0 }}>
            {project.content?.split("\n\n")[0]}
          </p>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginTop: "4px" }}>
          <ProgressBar raised={f.raised} goal={f.goal} />
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", color: "rgba(10,9,7,0.65)" }}>
            <span>
              {`${f.raised.toLocaleString(lang)} € ${t("spenden.raisedOf")} ${f.goal.toLocaleString(lang)} €`}
            </span>
            <span>
              {`${pct} % · ${f.donors} ${t("spenden.supportersLabel")}`}
            </span>
          </div>
        </div>
        <Link
          to={`/${lang}/${SPENDEN_PATH[lang]}/${f.slug}`}
          className={isCompleted ? "tl-pill tl-pill-ghost-dark" : "tl-pill tl-pill-dark"}
          style={{ marginTop: "8px", alignSelf: "flex-start", padding: featured ? "14px 28px" : "11px 0", width: featured ? "auto" : "100%", textAlign: "center" }}
        >
          {isCompleted ? t("spenden.ctaViewProject") : t("spenden.ctaSupport")}
        </Link>
      </div>
      {featured && (
        <ResponsiveImage
          src={f.image}
          alt={f.title}
          variant="light"
          style={{ width: "100%", height: "100%", minHeight: "320px", objectFit: "cover", display: "block" }}
        />
      )}
      {!featured && (
        <ResponsiveImage
          src={f.image}
          alt={f.title}
          variant="light"
          style={{ width: "100%", height: "150px", objectFit: "cover", display: "block", order: -1 }}
        />
      )}
    </div>
  );
}

export default function DonationOverview({ lang }) {
  const { t } = useTranslation();
  const allProjects = getSpendenProjectsForLang(lang);
  const projects = allProjects.filter((p) => p.frontmatter.status !== "completed");
  const completedProjects = allProjects.filter((p) => p.frontmatter.status === "completed");
  // frontmatter.featured lets an editor deliberately pin a project to the
  // top; falls back to file order (previous behaviour) if none is flagged.
  const featured = projects.find((p) => p.frontmatter.featured === true) ?? projects[0];
  const rest = projects.filter((p) => p !== featured);

  return (
    <>
      <SEOHead
        lang={lang}
        path={`/${SPENDEN_PATH[lang]}`}
        altPath={`/${SPENDEN_PATH[lang === "de" ? "en" : "de"]}`}
        title={t("spenden.metaTitle")}
        description={t("spenden.metaDescription")}
      />
      <JsonLd
        data={buildBreadcrumbJsonLd(
          [
            { name: t("nav.home"), path: "/" },
            { name: t("nav.spenden"), path: `/${SPENDEN_PATH[lang]}` },
          ],
          lang
        )}
      />
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
        <div className="tl-container" style={{ display: "flex", flexDirection: "column", gap: "56px", paddingTop: "64px", paddingBottom: "96px" }}>
          <div className="tl-grid-2" style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: "32px", alignItems: "center" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
              <p className="tl-eyebrow" style={{ margin: 0 }}>
                {t("spenden.overviewEyebrow")}
              </p>
              <h1 className="tl-hero-h1" style={{ fontFamily: "var(--font-serif)", fontSize: "44px", lineHeight: 1.1, color: "var(--color-cream)", margin: 0 }}>
                {t("spenden.overviewTitle")}
              </h1>
              <p style={{ fontSize: "16px", lineHeight: 1.7, color: "var(--color-cream-soft)", margin: 0, maxWidth: "56ch" }}>
                {t("spenden.overviewIntro")}
              </p>
            </div>
            <div style={{ position: "relative", width: "100%", aspectRatio: "4 / 3", borderRadius: "var(--radius-card-lg)", overflow: "hidden" }}>
              <ResponsiveImage
                src="/assets/brand/spenden.png"
                alt={
                  lang === "de"
                    ? "Handgeschriebenes Tagebuch mit Laterne und Feder aus der Spielwelt von Tales&Legends"
                    : "Handwritten journal with lantern and quill from the Tales&Legends game world"
                }
                priority
                style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
              />
            </div>
          </div>

          <section style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
            <h2 style={{ fontFamily: "var(--font-serif)", fontSize: "28px", color: "var(--color-cream)", margin: 0 }}>
              {t("spenden.impactTitle")}
            </h2>
            <p style={{ fontSize: "16px", lineHeight: 1.75, color: "var(--color-cream-soft)", margin: 0, maxWidth: "68ch" }}>
              {t("spenden.impactIntro")}
            </p>
            {/* Every figure here is one an existing donation project
              * already commits to (the `steps` frontmatter on the
              * betrieb and freiplatz projects) — not a second,
              * drift-prone set of example amounts. */}
            <dl style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(260px, 100%), 1fr))", gap: "20px", margin: 0 }}>
              {t("spenden.impactItems", { returnObjects: true }).map((item) => (
                <div key={item.amount} className="tl-card" style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  <dt style={{ fontFamily: "var(--font-serif)", fontSize: "24px", color: "var(--color-gold)" }}>{item.amount}</dt>
                  <dd style={{ fontSize: "16px", lineHeight: 1.65, color: "var(--color-cream-soft)", margin: 0 }}>{item.text}</dd>
                </div>
              ))}
            </dl>
          </section>

          <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: "24px", flexWrap: "wrap" }}>
              <h2 style={{ fontFamily: "var(--font-serif)", fontSize: "28px", color: "var(--color-cream)", margin: 0 }}>
                {t("nav.unterstuetzen")}
              </h2>
              <Link to={`/${lang}/${SUPPORT_PATH[lang]}`} style={{ fontSize: "14px", color: "var(--color-gold)" }}>
                {t("spenden.allSupportWaysLink")}
              </Link>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(260px, 100%), 1fr))", gap: "20px" }}>
              {Object.entries(SUPPORT_SUBPAGE_PATH).map(([topic, paths]) => (
                <Link
                  key={topic}
                  to={`/${lang}/${SUPPORT_PATH[lang]}/${paths[lang]}`}
                  className="tl-card tl-hover-raise"
                  style={{ display: "flex", flexDirection: "column", gap: "10px" }}
                >
                  <span style={{ fontFamily: "var(--font-serif)", fontSize: "20px", color: "var(--color-cream)" }}>
                    {t(`support.${topic}.title`)}
                  </span>
                  <span style={{ fontSize: "16px", lineHeight: 1.65, color: "var(--color-cream-soft)" }}>
                    {t(`support.${topic}.teaser`)}
                  </span>
                </Link>
              ))}
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "24px", paddingTop: "40px", borderTop: "1px solid rgba(254,250,239,0.1)" }}>
            <h2 style={{ fontFamily: "var(--font-serif)", fontSize: "28px", color: "var(--color-cream)", margin: 0 }}>
              {t("spenden.projectsListTitle")}
            </h2>

            {!featured && <p style={{ color: "var(--color-cream-soft)" }}>{t("spenden.noProjects")}</p>}

            {featured && <ProjectCard project={featured} lang={lang} t={t} featured />}

            {rest.length > 0 && (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(300px, 100%), 1fr))", gap: "24px" }}>
                {rest.map((project) => (
                  <ProjectCard key={project.frontmatter.slug} project={project} lang={lang} t={t} />
                ))}
              </div>
            )}
          </div>

          {completedProjects.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: "24px", paddingTop: "40px", borderTop: "1px solid rgba(254,250,239,0.1)" }}>
              <h2 style={{ fontFamily: "var(--font-serif)", fontSize: "28px", color: "var(--color-cream)", margin: 0 }}>
                {t("spenden.completedProjectsTitle")}
              </h2>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(300px, 100%), 1fr))", gap: "24px" }}>
                {completedProjects.map((project) => (
                  <ProjectCard key={project.frontmatter.slug} project={project} lang={lang} t={t} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
